import {
  ArchiveRestore,
  Check,
  ChevronDown,
  ChevronRight,
  History,
  MessageSquarePlus,
  Pencil,
  Pin,
  PinOff,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
} from '@/components/ui'
import { cn } from '@/utils'
import {
  useArchivedSessions,
  useChatLoaders,
  useChatMutations,
  useChatSessions,
  useCurrentSessionId,
  useHistoryPopoverOpen,
  useLoadingArchived,
  useLoadingSessions,
  useSessionFilter,
  useSessionSearch,
} from '../hooks'
import type { SessionFilter } from '../stores'
import type { ChatSession } from '../types'
import { matchesSessionFilter } from '../utils/sessionFilters'

interface ChatSessionsPopoverProps {
  slug: string
  onSelect: (sessionId: string) => void
  onCreateNew: () => Promise<void> | void
  onArchiveCurrent: () => void
  triggerClassName?: string
}

const RELATIVE_TIME = new Intl.RelativeTimeFormat(undefined, {
  numeric: 'auto',
})

function relativeTimeFor(iso: string): string {
  const target = new Date(iso).getTime()
  if (Number.isNaN(target)) return ''
  const diffMs = target - Date.now()
  const absSeconds = Math.abs(diffMs) / 1000

  if (absSeconds < 60) return 'just now'
  const minutes = Math.round(diffMs / 60000)
  if (Math.abs(minutes) < 60) return RELATIVE_TIME.format(minutes, 'minute')
  const hours = Math.round(diffMs / 3_600_000)
  if (Math.abs(hours) < 24) return RELATIVE_TIME.format(hours, 'hour')
  const days = Math.round(diffMs / 86_400_000)
  if (Math.abs(days) < 30) return RELATIVE_TIME.format(days, 'day')
  const months = Math.round(diffMs / (30 * 86_400_000))
  return RELATIVE_TIME.format(months, 'month')
}

function titleFor(session: ChatSession): string {
  if (session.title?.trim()) return session.title
  return 'New chat'
}

// Who started the thread. Own threads read "You"; teammates' threads show the
// local part of their email so the list stays scannable without going wide.
function ownerLabelFor(session: ChatSession): string {
  if (session.mine) return 'You'
  const email = session.createdByEmail
  if (!email) return 'Unknown'
  return email.split('@')[0]
}

function matchesSearch(session: ChatSession, query: string): boolean {
  if (!query) return true
  const needle = query.toLowerCase()
  return (
    titleFor(session).toLowerCase().includes(needle) ||
    (session.createdByEmail?.toLowerCase().includes(needle) ?? false)
  )
}

// Bucket sessions by recency so the list reads like a journal — most recent
// activity first under clear date labels. Already newest-first from the store.
type SessionGroup = { label: string; sessions: ChatSession[] }

const DAY_MS = 86_400_000

function groupSessionsByRecency(sessions: ChatSession[]): SessionGroup[] {
  const today: ChatSession[] = []
  const yesterday: ChatSession[] = []
  const lastWeek: ChatSession[] = []
  const lastMonth: ChatSession[] = []
  const older: ChatSession[] = []
  const now = Date.now()

  for (const session of sessions) {
    const age = now - new Date(session.updatedAt).getTime()
    if (age < DAY_MS) today.push(session)
    else if (age < 2 * DAY_MS) yesterday.push(session)
    else if (age < 7 * DAY_MS) lastWeek.push(session)
    else if (age < 30 * DAY_MS) lastMonth.push(session)
    else older.push(session)
  }

  return [
    { label: 'Today', sessions: today },
    { label: 'Yesterday', sessions: yesterday },
    { label: 'Previous 7 days', sessions: lastWeek },
    { label: 'Previous 30 days', sessions: lastMonth },
    { label: 'Older', sessions: older },
  ].filter((group) => group.sessions.length > 0)
}

const FILTER_TABS: { value: SessionFilter; label: string }[] = [
  { value: 'mine', label: 'Mine' },
  { value: 'team', label: 'Team' },
  { value: 'all', label: 'All' },
]

export default function ChatSessionsPopover({
  slug,
  onSelect,
  onCreateNew,
  onArchiveCurrent,
  triggerClassName,
}: ChatSessionsPopoverProps) {
  const open = useHistoryPopoverOpen()
  const sessions = useChatSessions()
  const currentId = useCurrentSessionId()
  const loading = useLoadingSessions()
  const filter = useSessionFilter()
  const search = useSessionSearch()
  const {
    archiveSession,
    renameSession,
    pinSession,
    unpinSession,
    setHistoryPopoverOpen,
    setSessionFilter,
    setSessionSearch,
  } = useChatMutations()

  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameDraft, setRenameDraft] = useState('')

  async function handleCreate() {
    setHistoryPopoverOpen(false)
    await onCreateNew()
  }

  function handleSelect(sessionId: string) {
    setHistoryPopoverOpen(false)
    onSelect(sessionId)
  }

  async function handleArchive(sessionId: string, event: React.MouseEvent) {
    event.stopPropagation()
    const wasCurrent = currentId === sessionId
    await archiveSession(slug, sessionId)
    if (wasCurrent) onArchiveCurrent()
  }

  function handleTogglePin(session: ChatSession, event: React.MouseEvent) {
    event.stopPropagation()
    if (session.pinned) {
      void unpinSession(slug, session.id)
    } else {
      void pinSession(slug, session.id)
    }
  }

  function handleStartRename(session: ChatSession, event: React.MouseEvent) {
    event.stopPropagation()
    setRenamingId(session.id)
    setRenameDraft(session.title ?? '')
  }

  function handleCancelRename() {
    setRenamingId(null)
    setRenameDraft('')
  }

  async function handleCommitRename(sessionId: string) {
    const trimmed = renameDraft.trim()
    if (!trimmed) {
      handleCancelRename()
      return
    }
    await renameSession(slug, sessionId, trimmed)
    handleCancelRename()
  }

  // The Mine/Team/All filter governs the whole list, pinned included, so
  // "Team" never shows your own threads (your active one or a pinned one).
  // Pinned threads still sort to the top within the filtered set. Search
  // narrows it. Computed here so the sections below stay declarative.
  const { pinned, groups } = useMemo(() => {
    const visible = sessions.filter(
      (session) =>
        matchesSearch(session, search) && matchesSessionFilter(session, filter),
    )
    const pinnedSessions = visible.filter((session) => session.pinned)
    const rest = visible.filter((session) => !session.pinned)
    return { pinned: pinnedSessions, groups: groupSessionsByRecency(rest) }
  }, [sessions, search, filter])

  const hasSessions = !loading && sessions.length > 0
  const hasVisibleResults = pinned.length > 0 || groups.length > 0

  function renderRow(session: ChatSession) {
    const isActive = currentId === session.id
    const isRenaming = renamingId === session.id
    return (
      <li
        key={session.id}
        className={cn(
          'group/row relative flex w-full items-center gap-1 rounded-lg px-2 py-2 text-[13px] transition-colors',
          'hover:bg-foreground/[0.04]',
          isActive && 'bg-foreground/[0.07] hover:bg-foreground/[0.07]',
        )}
      >
        {isActive && (
          <span
            aria-hidden="true"
            className="absolute inset-y-2 left-0 w-0.5 rounded-r bg-primary"
          />
        )}
        {isRenaming ? (
          <RenameRow
            draft={renameDraft}
            onChange={setRenameDraft}
            onCommit={() => handleCommitRename(session.id)}
            onCancel={handleCancelRename}
          />
        ) : (
          <SessionRow
            session={session}
            onSelect={() => handleSelect(session.id)}
            onTogglePin={(event) => handleTogglePin(session, event)}
            onStartRename={(event) => handleStartRename(session, event)}
            onArchive={(event) => handleArchive(session.id, event)}
          />
        )}
      </li>
    )
  }

  return (
    <Popover open={open} onOpenChange={setHistoryPopoverOpen}>
      <Tooltip content="Conversation history">
        <PopoverTrigger asChild>
          <Button
            type="button"
            theme="ghost"
            size="sm"
            isIcon
            aria-label="Conversation history"
            className={cn('text-muted-foreground', triggerClassName)}
          >
            <History />
          </Button>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="flex max-h-[70vh] w-96 flex-col overflow-hidden p-0"
      >
        <header className="flex items-center justify-between border-b px-3 py-2">
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Conversations
          </h3>
          <Button
            onClick={handleCreate}
            size="sm"
            theme="ghost"
            className="h-7 gap-1 px-2 text-xs"
          >
            <Plus className="h-3.5 w-3.5" />
            New
          </Button>
        </header>

        <div className="flex flex-col gap-2 border-b px-3 py-2">
          <FilterTabs value={filter} onChange={setSessionFilter} />
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => setSessionSearch(event.target.value)}
              placeholder="Search this case's chats…"
              className="h-7 pl-7 text-[13px]"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto py-1">
          {loading && (
            <p className="px-3 py-3 text-xs text-muted-foreground">Loading…</p>
          )}

          {!loading && sessions.length === 0 && (
            <EmptySessions onCreate={handleCreate} />
          )}

          {hasSessions && !hasVisibleResults && (
            <p className="px-3 py-6 text-center text-xs text-muted-foreground">
              No conversations match.
            </p>
          )}

          {pinned.length > 0 && (
            <section className="px-1 pb-1">
              <SectionLabel>
                <Pin className="h-3 w-3" aria-hidden />
                Pinned
              </SectionLabel>
              <ul className="flex flex-col gap-0.5">{pinned.map(renderRow)}</ul>
            </section>
          )}

          {groups.map((group) => (
            <section key={group.label} className="px-1 pb-1">
              <SectionLabel>{group.label}</SectionLabel>
              <ul className="flex flex-col gap-0.5">
                {group.sessions.map(renderRow)}
              </ul>
            </section>
          ))}

          {hasSessions && (
            <ArchivedSection
              slug={slug}
              search={search}
              onSelect={handleSelect}
            />
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h4 className="flex items-center gap-1 px-3 pb-1 pt-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">
      {children}
    </h4>
  )
}

function FilterTabs({
  value,
  onChange,
}: {
  value: SessionFilter
  onChange: (filter: SessionFilter) => void
}) {
  return (
    <div className="flex items-center gap-0.5 rounded-lg bg-foreground/[0.04] p-0.5">
      {FILTER_TABS.map((tab) => (
        <button
          key={tab.value}
          type="button"
          onClick={() => onChange(tab.value)}
          aria-pressed={value === tab.value}
          className={cn(
            'flex-1 cursor-pointer rounded-md px-2 py-1 text-xs font-medium transition-colors',
            value === tab.value
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {tab.label}
        </button>
      ))}
    </div>
  )
}

function SessionRow({
  session,
  onSelect,
  onTogglePin,
  onStartRename,
  onArchive,
}: {
  session: ChatSession
  onSelect: () => void
  onTogglePin: (event: React.MouseEvent) => void
  onStartRename: (event: React.MouseEvent) => void
  onArchive: (event: React.MouseEvent) => void
}) {
  return (
    <>
      <button
        type="button"
        onClick={onSelect}
        className="flex min-w-0 flex-1 cursor-pointer flex-col items-start gap-0.5 text-left leading-tight"
      >
        <span className="w-full truncate font-medium">{titleFor(session)}</span>
        <span className="truncate text-[11px] text-muted-foreground">
          {ownerLabelFor(session)}, {relativeTimeFor(session.updatedAt)}
        </span>
      </button>
      <ActionIcon
        label={session.pinned ? 'Unpin conversation' : 'Pin conversation'}
        onClick={onTogglePin}
        icon={
          session.pinned ? (
            <PinOff className="h-3.5 w-3.5" />
          ) : (
            <Pin className="h-3.5 w-3.5" />
          )
        }
        // A pin stays visible (not hover-only) so the case's curated threads
        // read as pinned at a glance. Pinning is open to any member.
        alwaysVisible={session.pinned}
      />
      {/* Renaming and archiving are owner-only — they reshape someone's thread,
          so they only show on your own. The server enforces this too. */}
      {session.mine && (
        <>
          <ActionIcon
            label="Rename conversation"
            onClick={onStartRename}
            icon={<Pencil className="h-3.5 w-3.5" />}
          />
          <ActionIcon
            label="Archive conversation"
            onClick={onArchive}
            icon={<Trash2 className="h-3.5 w-3.5" />}
            destructive
          />
        </>
      )}
    </>
  )
}

// The Archived section is lazy: it only fetches the (potentially long) archived
// list when the user opens it, and keeps it out of the main scan otherwise.
function ArchivedSection({
  slug,
  search,
  onSelect,
}: {
  slug: string
  search: string
  onSelect: (sessionId: string) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const [loadedOnce, setLoadedOnce] = useState(false)
  const archived = useArchivedSessions()
  const loading = useLoadingArchived()
  const { fetchArchivedSessions } = useChatLoaders()
  const { unarchiveSession } = useChatMutations()

  function handleToggle() {
    const next = !expanded
    setExpanded(next)
    if (next && !loadedOnce) {
      setLoadedOnce(true)
      void fetchArchivedSessions(slug)
    }
  }

  const visible = useMemo(
    () => archived.filter((session) => matchesSearch(session, search)),
    [archived, search],
  )

  return (
    <section className="border-t px-1 pb-1 pt-1">
      <button
        type="button"
        onClick={handleToggle}
        className="flex w-full cursor-pointer items-center gap-1 rounded-md px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70 hover:text-foreground"
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        Archived
        {loadedOnce && !loading ? ` (${archived.length})` : ''}
      </button>

      {expanded && (
        <div>
          {loading && (
            <p className="px-3 py-2 text-xs text-muted-foreground">Loading…</p>
          )}
          {!loading && archived.length === 0 && (
            <p className="px-3 py-2 text-xs text-muted-foreground">
              Nothing archived.
            </p>
          )}
          {!loading && archived.length > 0 && visible.length === 0 && (
            <p className="px-3 py-2 text-xs text-muted-foreground">
              No archived conversations match.
            </p>
          )}
          <ul className="flex flex-col gap-0.5">
            {visible.map((session) => (
              <li
                key={session.id}
                className="group/row flex w-full items-center gap-1 rounded-lg px-2 py-2 text-[13px] text-muted-foreground transition-colors hover:bg-foreground/[0.04]"
              >
                <button
                  type="button"
                  onClick={() => onSelect(session.id)}
                  className="flex min-w-0 flex-1 cursor-pointer flex-col items-start gap-0.5 text-left leading-tight"
                >
                  <span className="w-full truncate font-medium">
                    {titleFor(session)}
                  </span>
                  <span className="truncate text-[11px]">
                    {ownerLabelFor(session)},{' '}
                    {session.archivedAt
                      ? `archived ${relativeTimeFor(session.archivedAt)}`
                      : ''}
                  </span>
                </button>
                {session.mine && (
                  <ActionIcon
                    label="Restore conversation"
                    onClick={(event) => {
                      event.stopPropagation()
                      void unarchiveSession(slug, session.id)
                    }}
                    icon={<ArchiveRestore className="h-3.5 w-3.5" />}
                  />
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

function ActionIcon({
  label,
  onClick,
  icon,
  destructive,
  alwaysVisible,
}: {
  label: string
  onClick: (event: React.MouseEvent) => void
  icon: React.ReactNode
  destructive?: boolean
  alwaysVisible?: boolean
}) {
  return (
    <Tooltip content={label} side="top">
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        className={cn(
          'flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-all',
          'hover:bg-foreground/[0.06] hover:text-foreground',
          destructive && 'hover:bg-destructive/10 hover:text-destructive',
          alwaysVisible
            ? 'text-foreground'
            : 'opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        )}
      >
        {icon}
      </button>
    </Tooltip>
  )
}

const RENAME_ACTION_BUTTON_CLASSES =
  'flex h-7 w-7 cursor-pointer items-center justify-center rounded-md hover:bg-foreground/[0.06] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'

function RenameRow({
  draft,
  onChange,
  onCommit,
  onCancel,
}: {
  draft: string
  onChange: (value: string) => void
  onCommit: () => void
  onCancel: () => void
}) {
  return (
    <>
      <Input
        autoFocus
        value={draft}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') onCommit()
          if (event.key === 'Escape') onCancel()
        }}
        className="h-7 flex-1 text-[13px]"
      />
      <button
        type="button"
        onClick={onCommit}
        aria-label="Save"
        className={cn(RENAME_ACTION_BUTTON_CLASSES, 'text-emerald-600')}
      >
        <Check className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={onCancel}
        aria-label="Cancel rename"
        className={cn(RENAME_ACTION_BUTTON_CLASSES, 'text-muted-foreground')}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </>
  )
}

function EmptySessions({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 px-4 py-8 text-center">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
        <MessageSquarePlus className="h-5 w-5 text-muted-foreground" />
      </div>
      <div className="space-y-1">
        <p className="text-sm font-medium">No conversations yet</p>
        <p className="text-xs text-muted-foreground">
          Start a chat to ask Daystrom about your data.
        </p>
      </div>
      <Button size="sm" theme="secondary" onClick={onCreate} className="gap-1">
        <Plus className="h-3.5 w-3.5" />
        Start a new chat
      </Button>
    </div>
  )
}
